/**
 * JSON serialization utilities for trust boundaries.
 *
 * Use these types and functions only at true wire/ingress/egress boundaries:
 *   - JSON.parse / HTTP ingress / DB blob reads / localStorage reads
 *   - Opaque-by-design infrastructure (log sinks, archival writers, generic forwarders)
 *   - Parsers and guards that narrow unknown input into domain types
 *
 * Do NOT use JsonValue/JsonObject as domain model types — use concrete named
 * interfaces at the producer module instead.
 */

export type JsonPrimitive = string | number | boolean | null;

export type JsonValue = JsonPrimitive | JsonValue[] | JsonObject;

/** String-keyed JSON object (not an array). */
export type JsonObject = { [key: string]: JsonValue; };

/**
 * Structural guard for plain objects created with `Object` or `null` prototype.
 * Does not accept arrays, Dates, class instances, etc.
 */
export function isJsonObjectStructurally(value: unknown): value is JsonObject {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

export function toJsonValue(value: unknown): JsonValue {
    if (value === null) {
        return null;
    }
    if (typeof value === "string" || typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (Array.isArray(value)) {
        return value.map((entry) => (entry === undefined ? null : toJsonValue(entry)));
    }
    if (!isJsonObjectStructurally(value)) {
        return String(value);
    }

    const result: JsonObject = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) {
            result[key] = toJsonValue(entry);
        }
    }
    return result;
}

export function toJsonObject(value: unknown): JsonObject {
    const json = toJsonValue(value);
    return isJsonObjectStructurally(json) ? json : {};
}

export function compactJsonObject(entries: Iterable<readonly [string, JsonValue | undefined]>): JsonObject {
    const result: JsonObject = {};
    for (const [key, value] of entries) {
        if (value !== undefined) {
            result[key] = value;
        }
    }
    return result;
}

// ---------------------------------------------------------------------------
// Value readers — narrow one field of parsed JSON, or say it is absent. The
// workflow projections declared these privately, once per domain.
// ---------------------------------------------------------------------------

/** A non-empty string, or undefined. */
export function stringValue(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/** A finite number, or undefined. */
export function numberValue(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/** A boolean, or undefined. */
export function boolValue(value: unknown): boolean | undefined {
    return typeof value === "boolean" ? value : undefined;
}
